import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Shield, Zap, BadgePercent } from "lucide-react";

const PARTNERS = [
  { name: "Hearth", desc: "Personal loans up to $250k · soft-pull prequalification", terms: "2–12 years · rates from 7.99% APR" },
  { name: "Synchrony HOME", desc: "Promotional financing at select partners · hard credit pull", terms: "6–60 months depending on offer" },
  { name: "GreenSky", desc: "Same-day approvals · no prepayment penalties · hard credit pull", terms: "Up to $100k · 84-month terms" },
];

const HFS_APPLY_URL = "https://apply.hfsfinancial.net/prequalification";
const HFS_INFO_URL = "https://www.hfsfinancial.net/home-improvement-loans/kitchen-remodel-loans/";

export default function Financing() {
  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
      <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Financing</div>
      <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-4 max-w-5xl leading-[1.0]">
        Build now. Pay the way<br />that best suits you.
      </h1>
      <p className="text-zinc-600 max-w-2xl mt-8 leading-relaxed text-lg">
        We work with HFS Financial, an established home-improvement lender so you can focus on the design—not the math.
        Their quick soft-credit prequalification won&apos;t affect your credit score and lets you review real financing options in about 60 seconds.
      </p>

      {/* Featured partner — HFS Financial */}
      <section
        className="mt-14 border border-zinc-900 bg-zinc-950 text-white grid md:grid-cols-12 gap-0 overflow-hidden"
        data-testid="finance-featured-hfs"
      >
        <div className="md:col-span-7 p-8 md:p-12 lg:p-14">
          <div className="flex items-center gap-3">
            <span className="inline-block text-[10px] tracking-[0.3em] uppercase bg-white text-zinc-950 px-3 py-1.5">
              Featured partner
            </span>
            <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-400">Kitchen remodel financing</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-light mt-6 leading-[1.05]">
            HFS Financial.<br />
            <span className="text-zinc-400">You dream it, we finance it.</span>
          </h2>
          <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl">
            Personal loans tailored to kitchen remodels — no home equity required, no appraisal,
            no lien on your house. Check your rate in 60 seconds without any impact to your credit.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5 max-w-xl">
            <div className="flex items-start gap-3">
              <BadgePercent size={18} className="mt-0.5 text-white shrink-0" />
              <div>
                <div className="text-sm font-medium">Fixed rates from 7.8%</div>
                <div className="text-xs text-zinc-400 mt-1">Terms 1–30 years · no prepayment penalty</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Zap size={18} className="mt-0.5 text-white shrink-0" />
              <div>
                <div className="text-sm font-medium">Same-day prequalification</div>
                <div className="text-xs text-zinc-400 mt-1">Funds available within 48 hours of approval</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield size={18} className="mt-0.5 text-white shrink-0" />
              <div>
                <div className="text-sm font-medium">Soft credit check</div>
                <div className="text-xs text-zinc-400 mt-1">Won&apos;t impact your credit score</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check size={18} className="mt-0.5 text-white shrink-0" />
              <div>
                <div className="text-sm font-medium">Up to $450,000</div>
                <div className="text-xs text-zinc-400 mt-1">Direct-to-consumer — funds go to you</div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <a
              href={HFS_APPLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-zinc-950 px-7 py-4 text-sm hover:bg-zinc-200 transition"
              data-testid="finance-hfs-check-rate"
            >
              Check my rate <ArrowUpRight size={16} />
            </a>
            <a
              href={HFS_INFO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white px-3 py-4 transition"
              data-testid="finance-hfs-learn-more"
            >
              Learn more about HFS Financial <ArrowUpRight size={14} />
            </a>
          </div>
          <p className="mt-4 text-[11px] text-zinc-500">
            Opens in a new tab · 4.7★ on Trustpilot (2,400+ reviews) · 100,000+ homeowners funded
          </p>
        </div>

        <div className="md:col-span-5 bg-zinc-900 border-t md:border-t-0 md:border-l border-zinc-800 p-8 md:p-12">
          <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-400">How it works</div>
          <ol className="mt-6 space-y-5">
            {[
              { n: "01", t: "Inquire online", d: "60-second soft-credit check — no impact to your score." },
              { n: "02", t: "Get prequalified", d: "See real rates instantly or talk to an HFS specialist." },
              { n: "03", t: "Verify", d: "Securely upload a few basic documents." },
              { n: "04", t: "Get funded", d: "Funds disbursed directly to you, typically within 48 hours." },
            ].map((s) => (
              <li key={s.n} className="flex gap-4">
                <span className="font-display text-2xl text-zinc-600 leading-none mt-0.5">{s.n}</span>
                <div>
                  <div className="text-sm font-medium text-white">{s.t}</div>
                  <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <div className="mt-20 mb-8 flex items-end justify-between gap-6">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Also available</div>
          <h2 className="font-display text-3xl md:text-4xl font-light mt-3">Other lender options.</h2>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {PARTNERS.map((p) => (
          <div key={p.name} className="border border-zinc-200 p-8" data-testid={`finance-partner-${p.name.toLowerCase()}`}>
            <div className="font-display text-2xl">{p.name}</div>
            <p className="text-zinc-700 mt-3">{p.desc}</p>
            <div className="text-xs text-zinc-500 mt-4 tracking-wide">{p.terms}</div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-12 mt-20">
        <div>
          <div className="font-display text-3xl">What we promise.</div>
          <ul className="mt-6 space-y-3 text-zinc-700">
            {["See your available financing options before you commit to anything.","Simple application process with fast financing decisions.","Clear financing options with straightforward monthly-payment estimates.","No obligation to move forward after checking your eligibility."].map((t) => (
              <li key={t} className="flex items-start gap-3"><Check size={16} className="mt-1" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="border border-zinc-200 p-10 bg-white">
          <div className="font-display text-2xl">Ready to talk numbers?</div>
          <p className="text-zinc-600 mt-3">Book a free consultation and we&apos;ll connect you with the right lender — no commitment required.</p>
          <Link to="/booking" className="inline-flex mt-6 bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition" data-testid="finance-cta">Book a consultation</Link>
        </div>
      </div>

      <p className="text-xs text-zinc-500 mt-16 max-w-3xl leading-relaxed">
        Sleek Renovations is not a lender. All financing is offered by third-party partners and subject to
        credit approval. Rates, terms and amounts may vary based on creditworthiness. HFS Financial rates
        starting at 7.8% APR fixed are subject to change and based on credit profile.
      </p>
    </div>
  );
}
